// Sidebar Component
// Main navigation sidebar with professional styling

import React from 'react';
import { useTheme } from '../../design-system/ThemeProvider';
import { NavigationSection } from './NavigationSection';
import { NavigationIcon } from './NavigationIcon';
import { useNavigation } from './useNavigation';
import { navigationConfig } from './navigationConfig';

interface SidebarProps {
  user?: {
    signInDetails?: {
      loginId?: string;
    };
    username?: string;
  };
  onSignOut?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ user, onSignOut }) => {
  const { theme } = useTheme();
  const { state, actions, helpers } = useNavigation(navigationConfig);

  const sidebarStyles = {
    width: state.isCollapsed ? '64px' : theme.componentSpacing.sidebar.width,
    backgroundColor: theme.componentTokens.navigation.sidebar.background,
    borderRight: `1px solid ${theme.componentTokens.navigation.sidebar.border}`,
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'fixed' as const,
    height: '100vh',
    zIndex: theme.zIndex.docked,
    transition: `width ${theme.transitions.duration.normal} ${theme.transitions.easing.ease}`,
    boxShadow: theme.componentTokens.navigation.sidebar.shadow
  };

  const logoSectionStyles = {
    padding: `${theme.spacing[8]} ${theme.spacing[6]}`,
    borderBottom: `1px solid ${theme.semanticColors.border.primary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: state.isCollapsed ? 'center' : 'space-between'
  };

  const navigationContentStyles = {
    flex: 1,
    padding: `${theme.spacing[6]} 0`,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const
  };

  const signOutSectionStyles = {
    padding: theme.spacing[6],
    borderTop: `1px solid ${theme.semanticColors.border.primary}`
  };

  const handleItemClick = (itemId: string, path?: string) => {
    actions.setActiveItem(itemId);
    // TODO: Add routing logic here when implementing React Router
    console.log(`Navigate to: ${path}`);
  };

  const handleToggleSidebar = () => {
    actions.toggleSidebar();
  };

  return (
    <aside style={sidebarStyles}>
      {/* Logo & User Info Section */}
      <div style={logoSectionStyles}>
        {!state.isCollapsed ? (
          <>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.semanticColors.text.primary,
                  letterSpacing: theme.typography.letterSpacing.tight
                }}
              >
                Minecraft Portal
              </h1>
              {user && (
                <p
                  style={{
                    margin: `${theme.spacing[2]} 0 0 0`,
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.semanticColors.text.secondary,
                    fontWeight: theme.typography.fontWeight.medium
                  }}
                >
                  {user.signInDetails?.loginId || user.username}
                </p>
              )}
            </div>
            <button
              onClick={handleToggleSidebar}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: theme.spacing[2],
                borderRadius: theme.borderRadius.md,
                color: theme.semanticColors.text.secondary,
                transition: theme.transitions.duration.fast
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.semanticColors.surface.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <NavigationIcon name="Menu" size={20} />
            </button>
          </>
        ) : (
          <button
            onClick={handleToggleSidebar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: theme.spacing[2],
              borderRadius: theme.borderRadius.md,
              color: theme.semanticColors.text.secondary,
              transition: theme.transitions.duration.fast
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.semanticColors.surface.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <NavigationIcon name="Menu" size={20} />
          </button>
        )}
      </div>

      {/* Navigation Content */}
      {!state.isCollapsed && (
        <nav style={navigationContentStyles}>
          {navigationConfig.sections.map((section) => (
            <NavigationSection
              key={section.id}
              section={section}
              isExpanded={helpers.isSectionExpanded(section.id)}
              activeItemId={state.activeItemId}
              onToggleSection={() => actions.toggleSection(section.id)}
              onItemClick={handleItemClick}
              onItemToggle={actions.toggleItem}
              expandedItems={state.expandedItems}
            />
          ))}
        </nav>
      )}

      {/* Sign Out Section */}
      {!state.isCollapsed && onSignOut && (
        <div style={signOutSectionStyles}>
          <button
            onClick={onSignOut}
            style={{
              width: '100%',
              padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
              backgroundColor: theme.semanticColors.surface.secondary,
              border: `1px solid ${theme.semanticColors.border.primary}`,
              borderRadius: theme.borderRadius.lg,
              color: theme.semanticColors.text.primary,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer',
              transition: theme.transitions.duration.fast,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing[2]
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.semanticColors.surface.hover;
              e.currentTarget.style.borderColor = theme.semanticColors.border.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.semanticColors.surface.secondary;
              e.currentTarget.style.borderColor = theme.semanticColors.border.primary;
            }}
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Collapsed State Icons */}
      {state.isCollapsed && (
        <nav style={{ ...navigationContentStyles, padding: `${theme.spacing[4]} 0` }}>
          {navigationConfig.sections.flatMap(section => 
            section.items.map(item => (
              <div
                key={item.id}
                style={{
                  padding: theme.spacing[3],
                  margin: `${theme.spacing[1]} ${theme.spacing[2]}`,
                  borderRadius: theme.borderRadius.lg,
                  cursor: 'pointer',
                  transition: theme.transitions.duration.fast,
                  backgroundColor: helpers.isItemActive(item.id) 
                    ? theme.componentTokens.navigation.item.backgroundActive 
                    : 'transparent',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => handleItemClick(item.id, item.path)}
                onMouseEnter={(e) => {
                  if (!helpers.isItemActive(item.id)) {
                    e.currentTarget.style.backgroundColor = theme.semanticColors.surface.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!helpers.isItemActive(item.id)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title={item.label}
              >
                {item.icon && (
                  <NavigationIcon
                    name={item.icon}
                    size={20}
                    color={helpers.isItemActive(item.id) 
                      ? theme.componentTokens.navigation.item.textActive 
                      : theme.componentTokens.navigation.item.text
                    }
                  />
                )}
              </div>
            ))
          )}
        </nav>
      )}
    </aside>
  );
};